import { useRef, useState, useCallback, Fragment, type CSSProperties } from "react";
import { Trash2, RotateCcw, Plus, Eraser, ZoomIn, ZoomOut } from "lucide-react";
import {
  A4_W,
  A4_H,
  PALETTE,
  labelFor,
  DEFAULT_TEMPLATE,
  FONT_OPTIONS,
  DEFAULT_FONT_FAMILY,
  DEFAULT_BASE_FONT_SIZE,
  LINE_ITEM_COLUMNS,
  DEFAULT_LINE_ITEM_KEYS,
  type ComponentType,
  type PlacedComponent,
  type LayoutTemplate,
  type TemplateStyle,
} from "./templateTypes";

interface LayoutBuilderProps {
  value: LayoutTemplate | null;
  onChange: (t: LayoutTemplate) => void;
  company: any;
  /** Optionele externe zoom-controle (parent toont dan de zoom-knoppen). */
  zoom?: number;
  onZoomChange?: (z: number) => void;
}

export const DEFAULT_SCALE = 0.62;
const MINI_SCALE = 0.2;
const GRID = 10;
export const MIN_ZOOM = 0.3;
export const MAX_ZOOM = 1.5;

/**
 * Vervanger voor react-rnd (incompatibel met React 19: gebruikt de
 * verwijderde ReactDOM.findDOMNode). Pointer-events versie met live
 * drag en 4 hoekgrepen voor resize, inclusief bounds op het A4-canvas.
 */
function RndBox(props: {
  scale: number;
  position: { x: number; y: number };
  size: { width: number; height: number };
  selected: boolean;
  onDragStop: (pos: { x: number; y: number }) => void;
  onResizeStop: (r: { x: number; y: number; width: number; height: number }) => void;
  style: CSSProperties;
  onSelect: () => void;
  children: React.ReactNode;
}) {
  const [live, setLive] = useState<null | { x: number; y: number; w: number; h: number }>(null);
  const drag = useRef<null | { mode: "drag" | "resize"; dir?: string; px: number; py: number; x: number; y: number; w: number; h: number }>(null);

  const pos = live ?? { x: props.position.x, y: props.position.y };
  const w = live ? live.w : props.size.width;
  const h = live ? live.h : props.size.height;

  const start = (e: React.PointerEvent, mode: "drag" | "resize", dir?: string) => {
    e.preventDefault();
    e.stopPropagation();
    props.onSelect();
    drag.current = { mode, dir, px: e.clientX, py: e.clientY, x: props.position.x, y: props.position.y, w: props.size.width, h: props.size.height };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const dx = (e.clientX - d.px) / props.scale;
    const dy = (e.clientY - d.py) / props.scale;
    if (d.mode === "drag") {
      const x = Math.max(0, Math.min(A4_W - d.w, d.x + dx));
      const y = Math.max(0, Math.min(A4_H - d.h, d.y + dy));
      setLive({ x, y, w: d.w, h: d.h });
    } else {
      const dir = d.dir!;
      let { x, y, w, h } = d;
      if (dir.includes("e")) w = d.w + dx;
      if (dir.includes("w")) { w = d.w - dx; x = d.x + dx; }
      if (dir.includes("s")) h = d.h + dy;
      if (dir.includes("n")) { h = d.h - dy; y = d.y + dy; }
      w = Math.max(20, w);
      h = Math.max(16, h);
      x = Math.max(0, Math.min(A4_W - w, x));
      y = Math.max(0, Math.min(A4_H - h, y));
      setLive({ x, y, w, h });
    }
  };

  const end = () => {
    const d = drag.current;
    drag.current = null;
    if (!d || !live) { setLive(null); return; }
    const fin = live;
    setLive(null);
    if (d.mode === "drag") props.onDragStop({ x: fin.x, y: fin.y });
    else props.onResizeStop({ x: fin.x, y: fin.y, width: fin.w, height: fin.h });
  };

  return (
    <div
      className="absolute select-none touch-none"
      style={{ left: pos.x, top: pos.y, width: w, height: h, ...props.style, cursor: "grab" }}
      onPointerDown={(e) => start(e, "drag")}
      onPointerMove={onPointerMove}
      onPointerUp={end}
      onPointerCancel={end}
    >
      {props.children}
      {props.selected && (["nw", "ne", "sw", "se"] as const).map((dir) => (
        <div
          key={dir}
          className="absolute w-2.5 h-2.5 bg-white border border-emerald-500 touch-none"
          style={{
            left: dir.includes("w") ? -4 : undefined,
            right: dir.includes("e") ? -4 : undefined,
            top: dir.includes("n") ? -4 : undefined,
            bottom: dir.includes("s") ? -4 : undefined,
            cursor: dir === "nw" || dir === "se" ? "nwse-resize" : "nesw-resize",
          }}
          onPointerDown={(e) => start(e, "resize", dir)}
        />
      ))}
    </div>
  );
}

export function LayoutBuilder({ value, onChange, company, zoom: zoomProp, onZoomChange }: LayoutBuilderProps) {
  const components = value?.components ?? [];
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [gridOn, setGridOn] = useState(false);
  const [internalZoom, setInternalZoom] = useState(DEFAULT_SCALE);
  const zoom = zoomProp ?? internalZoom;
  const setZoom = (z: number) => {
    if (onZoomChange) onZoomChange(z);
    else setInternalZoom(z);
  };
  const canvasRef = useRef<HTMLDivElement>(null);

  const snap = (v: number) => (gridOn ? Math.round(v / GRID) * GRID : v);

  const selected = components.find((c) => c.id === selectedId) || null;
  const style: TemplateStyle = value?.style || { fontFamily: DEFAULT_FONT_FAMILY, baseFontSize: DEFAULT_BASE_FONT_SIZE };

  const emit = useCallback(
    (next: PlacedComponent[]) => onChange({ version: 1, style, components: next }),
    [onChange, style]
  );

  const addComponent = (type: ComponentType, x?: number, y?: number) => {
    const item = PALETTE.find((p) => p.type === type)!;
    const comp: PlacedComponent = {
      id: crypto.randomUUID(),
      type,
      x: snap(x ?? 40 + ((components.length * 24) % 200)),
      y: snap(y ?? 40 + ((components.length * 18) % 160)),
      w: item.defaultW,
      h: item.defaultH,
      props: type === "textBlock" ? { text: "", fontSize: 11, align: "left" } : undefined,
    };
    emit([...components, comp]);
    setSelectedId(comp.id);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const type = e.dataTransfer.getData("text/plain") as ComponentType;
    if (!type || !PALETTE.some((p) => p.type === type)) return;
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = Math.max(0, Math.min(A4_W - 40, (e.clientX - rect.left) / zoom));
    const y = Math.max(0, Math.min(A4_H - 30, (e.clientY - rect.top) / zoom));
    addComponent(type, x, y);
  };

  const updateComp = (id: string, patch: Partial<PlacedComponent>) =>
    emit(components.map((c) => (c.id === id ? { ...c, ...patch } : c)));

  const updateProps = (id: string, key: string, val: any) =>
    emit(
      components.map((c) =>
        c.id === id ? { ...c, props: { ...(c.props || {}), [key]: val } } : c
      )
    );

  /** Zichtbare standaardkolommen van het regeloverzicht (props.columns whitelist). */
  const lineColVisible = (comp: PlacedComponent): string[] =>
    Array.isArray(comp.props?.columns) && comp.props!.columns!.length
      ? comp.props!.columns!
      : DEFAULT_LINE_ITEM_KEYS;

  /** Toggle een regeloverzicht-koloom aan/uit in props.columns. */
  const toggleLineCol = (id: string, key: string) =>
    emit(
      components.map((c) => {
        if (c.id !== id) return c;
        const cur = new Set(lineColVisible(c));
        if (cur.has(key)) cur.delete(key);
        else cur.add(key);
        // vaste volgorde aanhouden
        const ordered = DEFAULT_LINE_ITEM_KEYS.filter((k) => cur.has(k));
        return { ...c, props: { ...(c.props || {}), columns: ordered } };
      })
    );

  /** Verwijder een per-component eigenschap zodat deze terugvalt op het documentstandaard. */
  const clearProp = (id: string, key: string) =>
    emit(
      components.map((c) => {
        if (c.id !== id || !c.props) return c;
        const next = { ...c.props };
        delete next[key];
        return { ...c, props: next };
      })
    );

  const removeComp = (id: string) => {
    emit(components.filter((c) => c.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  return (
    <div className="flex flex-col lg:flex-row gap-5">
      {/* Palet */}
      <div className="lg:w-60 shrink-0 space-y-3">
        <p className="text-xs font-bold text-slate-600 uppercase tracking-wide">Componenten</p>
        <p className="text-xs text-slate-500">Sleep een blok op het A4 of klik om toe te voegen.</p>
        <div className="grid grid-cols-2 lg:grid-cols-1 gap-2">
          {PALETTE.map((item) => (
            <button
              key={item.type}
              draggable
              onDragStart={(e) => e.dataTransfer.setData("text/plain", item.type)}
              onClick={() => addComponent(item.type)}
              className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-left text-sm font-medium text-slate-700 hover:border-brand-400 hover:bg-brand-50/40 cursor-grab active:cursor-grabbing transition-colors"
            >
              <Plus size={18} className="text-brand-500 shrink-0" />
              {item.label}
            </button>
          ))}
        </div>
        <div className="flex flex-col gap-2 pt-2 border-t border-slate-100">
          <button
            onClick={() => { onChange(DEFAULT_TEMPLATE); setSelectedId(null); }}
            className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
          >
            <RotateCcw size={18} /> Resetten naar standaard
          </button>
          <button
            onClick={() => { onChange({ version: 1, style, components: [] }); setSelectedId(null); }}
            className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
          >
            <Eraser size={18} /> Leegmaken
          </button>
        </div>

        {/* Raster voor uitlijnen */}
        <div className="pt-3 border-t border-slate-100 space-y-2">
          <p className="text-xs font-bold text-slate-600 uppercase tracking-wide">Uitlijnen</p>
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={gridOn}
              onChange={(e) => setGridOn(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
            />
            Raster tonen & uitlijnen
          </label>
          <p className="text-[11px] text-slate-400 leading-relaxed">
            Bij aan staat er een 10px-raster op het A4; blokken kleven aan het raster bij slepen en vergroten.
          </p>
        </div>
      </div>

      {/* Center: A4 canvas (zoom-knoppen zitten in de parent-balk indien onZoomChange is meegegeven) */}
      <div className="flex-1 flex flex-col gap-3 min-w-0">
        {!onZoomChange && (
          <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2">
            <span className="text-xs font-bold text-slate-600 uppercase tracking-wide">Zoom</span>
            <button
              onClick={() => setZoom(Math.max(MIN_ZOOM, +(zoom - 0.1).toFixed(2)))}
              className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
              title="Uitzoomen"
            >
              <ZoomOut size={18} />
            </button>
            <span className="text-sm tabular-nums w-12 text-center text-slate-700">{Math.round(zoom * 100)}%</span>
            <button
              onClick={() => setZoom(Math.min(MAX_ZOOM, +(zoom + 0.1).toFixed(2)))}
              className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
              title="Inzoomen"
            >
              <ZoomIn size={18} />
            </button>
            <button
              onClick={() => setZoom(DEFAULT_SCALE)}
              className="ml-auto text-[11px] text-brand-600 hover:text-brand-700"
              title="Terug naar standaard"
            >
              reset
            </button>
          </div>
        )}
        {/* A4 canvas */}
        <div className="flex-1 overflow-auto bg-slate-100/70 rounded-2xl p-6 flex justify-center">
        <div style={{ width: A4_W * zoom, height: A4_H * zoom, position: "relative" }}>
        <div
          ref={canvasRef}
          className="a4-canvas bg-white shadow-lg"
          style={{
            width: A4_W,
            height: A4_H,
            transform: `scale(${zoom})`,
            transformOrigin: "top left",
            position: "absolute",
            top: 0,
            left: 0,
            backgroundImage: gridOn
              ? "linear-gradient(#e2e8f0 1px, transparent 1px), linear-gradient(90deg, #e2e8f0 1px, transparent 1px)"
              : undefined,
            backgroundSize: gridOn ? `${GRID}px ${GRID}px` : undefined,
          }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          onClick={(e) => { if (e.target === e.currentTarget) setSelectedId(null); }}
        >
          {components.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center text-slate-300 text-lg pointer-events-none">
              Sleep componenten hierheen
            </div>
          )}
          {components.map((comp) => (
            <RndBox
              key={comp.id}
              scale={zoom}
              position={{ x: comp.x, y: comp.y }}
              size={{ width: comp.w, height: comp.h }}
              selected={selectedId === comp.id}
              onDragStop={(p) => updateComp(comp.id, { x: snap(p.x), y: snap(p.y) })}
              onResizeStop={(r) =>
                updateComp(comp.id, {
                  x: snap(r.x),
                  y: snap(r.y),
                  w: Math.max(40, snap(r.width)),
                  h: Math.max(24, snap(r.height)),
                })
              }
              style={{
                border: selectedId === comp.id ? "2px solid #10b981" : "1px dashed #cbd5e1",
                background: "rgba(255,255,255,0.6)",
                fontSize: 11,
                overflow: "hidden",
              }}
              onSelect={() => setSelectedId(comp.id)}
            >
              <div className="w-full h-full pointer-events-none p-1 overflow-hidden" style={componentBoxStyle(comp)}>
                <PreviewContent comp={comp} company={company} style={style} />
              </div>
            </RndBox>
          ))}
        </div>
        </div>
        </div>
      </div>

      {/* Eigenschappenpaneel */}
      <div className="lg:w-44 shrink-0">
        <div className="rounded-2xl border border-slate-200 bg-white p-3">
          <p className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-3">Eigenschappen</p>
          {!selected ? (
            <p className="text-sm text-slate-400">Selecteer een component op het A4 om aan te passen.</p>
          ) : (
            <div className="space-y-4">
              <div>
                <p className="text-sm font-semibold text-slate-800">{labelFor(selected.type)}</p>
                <button
                  onClick={() => removeComp(selected.id)}
                  className="mt-2 inline-flex items-center gap-1.5 text-sm text-rose-600 hover:text-rose-700"
                >
                  <Trash2 size={16} /> Verwijderen
                </button>
              </div>

              {/* Lettertype per component (voor alle componenten behalve logo) */}
              {selected.type !== "logo" && (
                <div>
                  <label className="text-xs font-medium text-slate-600">Lettertype</label>
                  <select
                    value={selected.props?.fontFamily || ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "") clearProp(selected.id, "fontFamily");
                      else updateProps(selected.id, "fontFamily", v);
                    }}
                    className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  >
                    <option value="">Gebruik documentstandaard</option>
                    {FONT_OPTIONS.map((f) => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Lettergrootte per component (titel heeft een eigen, groter, default) */}
              {selected.type !== "logo" && (
                <div>
                  <label className="text-xs font-medium text-slate-600">
                    Lettergrootte (px){selected.type === "docTitle" ? " — titel" : ""}
                  </label>
                  <input
                    type="number"
                    min={6}
                    max={48}
                    value={selected.props?.fontSize ?? ""}
                    placeholder={selected.type === "docTitle" ? "22 (standaard)" : `${style.baseFontSize ?? DEFAULT_BASE_FONT_SIZE} (standaard)`}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "") clearProp(selected.id, "fontSize");
                      else updateProps(selected.id, "fontSize", Number(v));
                    }}
                    className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  />
                  {selected.props?.fontSize != null && (
                    <button
                      onClick={() => clearProp(selected.id, "fontSize")}
                      className="mt-1 text-[11px] text-brand-600 hover:text-brand-700"
                    >
                      Gebruik standaard
                    </button>
                  )}
                </div>
              )}

              {/* Achtergrond & lijn — voor alle componenten */}
              <div className="space-y-3 pt-2 border-t border-slate-100">
                <p className="text-xs font-bold text-slate-600 uppercase tracking-wide">Achtergrond &amp; lijn</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs font-medium text-slate-600">Achtergrond</label>
                    <div className="mt-1 flex items-center gap-1.5">
                      <input
                        type="color"
                        value={selected.props?.bgColor || "#f8fafc"}
                        onChange={(e) => updateProps(selected.id, "bgColor", e.target.value)}
                        className="h-9 w-10 rounded-lg border border-slate-200"
                      />
                      <button
                        onClick={() => clearProp(selected.id, "bgColor")}
                        className="text-[11px] text-brand-600 hover:text-brand-700"
                      >
                        Geen
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Padding (px)</label>
                    <input
                      type="number"
                      min={0}
                      max={40}
                      value={selected.props?.padding ?? 0}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "" || v === "0") clearProp(selected.id, "padding");
                        else updateProps(selected.id, "padding", Number(v));
                      }}
                      className="mt-1 w-full h-9 rounded-lg border border-slate-200 px-2 text-sm"
                    />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-xs font-medium text-slate-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!selected.props?.borderWidth}
                    onChange={(e) => {
                      if (e.target.checked) updateProps(selected.id, "borderWidth", selected.props?.borderWidth || 1);
                      else clearProp(selected.id, "borderWidth");
                    }}
                    className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                  />
                  Rand / lijn tonen
                </label>
                {selected.props?.borderWidth ? (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs font-medium text-slate-600">Randkleur</label>
                      <input
                        type="color"
                        value={selected.props?.borderColor || "#1f2937"}
                        onChange={(e) => updateProps(selected.id, "borderColor", e.target.value)}
                        className="mt-1 w-full h-9 rounded-lg border border-slate-200"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600">Dikte (px)</label>
                      <input
                        type="number"
                        min={1}
                        max={10}
                        value={selected.props?.borderWidth ?? 1}
                        onChange={(e) => updateProps(selected.id, "borderWidth", Number(e.target.value))}
                        className="mt-1 w-full h-9 rounded-lg border border-slate-200 px-2 text-sm"
                      />
                    </div>
                  </div>
                ) : null}
                <p className="text-[11px] text-slate-400 leading-relaxed">
                  Geef een blok een gekleurde achtergrond (zoals het grijze klantblok) of een omlijning.
                </p>
              </div>

              {selected.type === "textBlock" && (
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-slate-600">Tekst</label>
                    <textarea
                      value={selected.props?.text || ""}
                      onChange={(e) => updateProps(selected.id, "text", e.target.value)}
                      rows={4}
                      placeholder="Bijv. betaalinstructie…"
                      className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Uitlijnen</label>
                    <select
                      value={selected.props?.align ?? "left"}
                      onChange={(e) => updateProps(selected.id, "align", e.target.value)}
                      className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    >
                      <option value="left">Links</option>
                      <option value="center">Midden</option>
                      <option value="right">Rechts</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Tabelkop-styling voor regeloverzicht */}
              {selected.type === "lineItems" && (
                <div className="space-y-3 pt-2 border-t border-slate-100">
                  <p className="text-xs font-bold text-slate-600 uppercase tracking-wide">Tabelkop</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs font-medium text-slate-600">Achtergrond</label>
                      <input
                        type="color"
                        value={selected.props?.headerBg || "#334155"}
                        onChange={(e) => updateProps(selected.id, "headerBg", e.target.value)}
                        className="mt-1 w-full h-9 rounded-lg border border-slate-200"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600">Tekstkleur</label>
                      <input
                        type="color"
                        value={selected.props?.headerColor || "#ffffff"}
                        onChange={(e) => updateProps(selected.id, "headerColor", e.target.value)}
                        className="mt-1 w-full h-9 rounded-lg border border-slate-200"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Kolom-zichtbaarheid voor regeloverzicht */}
              {selected.type === "lineItems" && (
                <div className="space-y-2 pt-2 border-t border-slate-100">
                  <p className="text-xs font-bold text-slate-600 uppercase tracking-wide">Kolommen in overzicht</p>
                  <div className="space-y-1">
                    {LINE_ITEM_COLUMNS.map((col) => {
                      const visible = lineColVisible(selected).includes(col.key);
                      return (
                        <label
                          key={col.key}
                          className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={visible}
                            onChange={() => toggleLineCol(selected.id, col.key)}
                            className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                          />
                          {col.label}
                        </label>
                      );
                    })}
                    <label className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selected.props?.showCustomColumns !== false}
                        onChange={(e) => updateProps(selected.id, "showCustomColumns", e.target.checked)}
                        className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                      />
                      Extra kolommen uit document
                    </label>
                  </div>
                  <p className="text-[11px] text-slate-400 leading-relaxed">
                    Vink aan welke kolommen op het document verschijnen. Extra kolommen
                    zijn de eigen kolommen die je per offerte/factuur toevoegt.
                  </p>
                </div>
              )}

              {/* Opmerkingen-blok: eigen label + vaste tekst, of leeg voor documentnotities */}
              {selected.type === "notes" && (
                <div className="space-y-3 pt-2 border-t border-slate-100">
                  <p className="text-xs font-bold text-slate-600 uppercase tracking-wide">Opmerkingen</p>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Label</label>
                    <input
                      type="text"
                      value={selected.props?.label ?? ""}
                      onChange={(e) => updateProps(selected.id, "label", e.target.value)}
                      placeholder="Opmerkingen"
                      className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Vaste tekst</label>
                    <textarea
                      value={selected.props?.text || ""}
                      onChange={(e) => updateProps(selected.id, "text", e.target.value)}
                      rows={4}
                      placeholder="Vaste tekst op elk document (bijv. betaalinstructie). Leeg = toont de notities uit het document."
                      className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              )}

              {selected.type !== "textBlock" && selected.type !== "lineItems" && selected.type !== "notes" && (
                <p className="text-xs text-slate-400 leading-relaxed">
                  Dit blok wordt automatisch gevuld met de gegevens uit het document
                  (bedrijf, klant, datum, totalen, enz.). Pas alleen de positie, grootte
                  en opmaak aan.
                </p>
              )}

              <div className="grid grid-cols-2 gap-2 text-xs text-slate-500 pt-2 border-t border-slate-100">
                <div>X: {Math.round(selected.x)}</div>
                <div>Y: {Math.round(selected.y)}</div>
                <div>B: {Math.round(selected.w)}</div>
                <div>H: {Math.round(selected.h)}</div>
              </div>
            </div>
          )}
        </div>

        {/* Live mini-preview van de opgeslagen A4-lay-out */}
        <div className="rounded-2xl border border-slate-200 bg-white p-3 mt-4">
          <p className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">Opgeslagen lay-out</p>
          {components.length === 0 ? (
            <p className="text-xs text-slate-400">Nog geen componenten.</p>
          ) : (
            <>
              <div className="rounded-lg bg-slate-100 p-2 flex justify-center">
                <div style={{ width: A4_W * MINI_SCALE, height: A4_H * MINI_SCALE, position: "relative" }}>
                  <div
                    style={{
                      width: A4_W,
                      height: A4_H,
                      transform: `scale(${MINI_SCALE})`,
                      transformOrigin: "top left",
                      position: "absolute",
                      top: 0,
                      left: 0,
                      background: "#fff",
                      boxShadow: "0 1px 4px rgba(0,0,0,.12)",
                    }}
                  >
                    {components.map((comp) => (
                      <div
                        key={comp.id}
                        onClick={(e) => { e.stopPropagation(); setSelectedId(comp.id); }}
                        title={labelFor(comp.type)}
                        style={{
                          position: "absolute",
                          left: comp.x,
                          top: comp.y,
                          width: comp.w,
                          height: comp.h,
                          outline: selectedId === comp.id ? "1px solid #10b981" : "none",
                          cursor: "pointer",
                          overflow: "hidden",
                          ...componentBoxStyle(comp),
                        }}
                      >
                        <PreviewContent comp={comp} company={company} style={style} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-[10px] text-slate-400 mt-1.5 text-center">Klik een blok om te selecteren.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** Per-component achtergrond + rand + padding (uit comp.props) — voor de
 *  WYSIWYG-weergave in de builder. Spiegel van backend componentBoxCss. */
function componentBoxStyle(comp: PlacedComponent): CSSProperties {
  const s: CSSProperties = { boxSizing: "border-box" };
  if (comp.props?.bgColor) s.background = comp.props.bgColor;
  const bw = Number(comp.props?.borderWidth || 0);
  if (bw > 0) s.border = `${bw}px solid ${comp.props?.borderColor || "#1f2937"}`;
  const pad = Number(comp.props?.padding || 0);
  if (pad > 0) s.padding = pad;
  return s;
}

/** Live voorbeeld van een component-inhoud in de builder (geen echte documentdata). */
function PreviewContent({ comp, company, style }: { comp: PlacedComponent; company: any; style: TemplateStyle }) {
  const c = company || {};
  const fontFamily = comp.props?.fontFamily || style.fontFamily || DEFAULT_FONT_FAMILY;
  const baseSize = comp.props?.fontSize ?? style.baseFontSize ?? DEFAULT_BASE_FONT_SIZE;
  const fontWrap = (extra?: CSSProperties): CSSProperties => ({
    fontFamily,
    fontSize: baseSize,
    color: "#1f2937",
    ...extra,
  });
  switch (comp.type) {
    case "logo":
      return c.logoUrl ? (
        <img src={`${import.meta.env.VITE_API_URL || ""}${c.logoUrl}`} alt="" className="max-w-full max-h-full object-contain" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-slate-300">[ logo ]</div>
      );
    case "companyAddress":
      return (
        <div style={fontWrap({ lineHeight: 1.25 })}>
          <strong style={{ fontSize: baseSize + 2 }}>{c.name || "Bedrijfsnaam"}</strong>
          <div className="text-slate-600">{c.address || "Straat 1"}</div>
          <div className="text-slate-600">{c.zipCode || "1234 AB"} {c.city || "Plaats"}</div>
          <div className="text-slate-400" style={{ fontSize: Math.max(baseSize - 2, 8) }}>KVK: {c.kvkNumber || "-"} | BTW: {c.vatNumber || "-"}</div>
        </div>
      );
    case "customerAddress":
      return (
        <div style={fontWrap({ lineHeight: 1.25 })}>
          <strong style={{ fontSize: baseSize + 2 }}>Klant-/leveranciersnaam</strong>
          <div className="text-slate-600">Straat 1</div>
          <div className="text-slate-600">1234 AB Plaats</div>
          <div className="text-slate-400" style={{ fontSize: Math.max(baseSize - 2, 8) }}>naam@email.com</div>
        </div>
      );
    case "docTitle":
      return <div style={fontWrap({ fontSize: comp.props?.fontSize || 22, fontWeight: 700, letterSpacing: 1 })}>FACTUUR / OFFERTE</div>;
    case "docNumber":
      return <div style={fontWrap()}><span className="text-slate-400" style={{ fontSize: Math.max(baseSize - 2, 8) }}>Nummer</span><br /><strong>FA-2026-0001</strong></div>;
    case "docDate":
      return <div style={fontWrap()}><span className="text-slate-400" style={{ fontSize: Math.max(baseSize - 2, 8) }}>Datum</span><br />01-08-2026</div>;
    case "docDueDate":
      return <div style={fontWrap()}><span className="text-slate-400" style={{ fontSize: Math.max(baseSize - 2, 8) }}>Vervaldatum / Geldig t/m</span><br />15-08-2026</div>;
    case "lineItems": {
      const headerBg = comp.props?.headerBg || "#334155";
      const headerColor = comp.props?.headerColor || "#ffffff";
      const cellFs = baseSize;
      const visibleKeys = Array.isArray(comp.props?.columns) && comp.props!.columns!.length
        ? comp.props!.columns!
        : DEFAULT_LINE_ITEM_KEYS;
      const showCustom = comp.props?.showCustomColumns !== false;
      const th = (label: string, align: "left" | "right") => (
        <th style={{ background: headerBg, color: headerColor, border: `1px solid ${headerBg}`, padding: "4px 6px", textAlign: align, fontWeight: 600, fontSize: cellFs }}>{label}</th>
      );
      const td = (align: "left" | "right", content: string) => (
        <td style={{ border: "1px solid #cbd5e1", padding: "3px 6px", textAlign: align, fontSize: cellFs }}>{content}</td>
      );
      const cols: { key: string; label: string; align: "left" | "right"; val: string }[] = [
        { key: "description", label: "Omschrijving", align: "left", val: "Regel 1" },
        { key: "quantity", label: "Aantal", align: "right", val: "1" },
        { key: "unitPrice", label: "Prijs", align: "right", val: "€ 0,00" },
        { key: "vat", label: "BTW %", align: "right", val: "21%" },
        { key: "vatAmount", label: "BTW bedrag", align: "right", val: "€ 0,00" },
        { key: "lineTotal", label: "Totaal", align: "right", val: "€ 0,00" },
      ];
      const headCells = (
        <>
          {cols.filter((c) => visibleKeys.includes(c.key)).map((c) => <Fragment key={c.key}>{th(c.label, c.align)}</Fragment>)}
          {showCustom && <Fragment>{th("Extra", "left")}</Fragment>}
        </>
      );
      return (
        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", fontFamily }}>
          <thead><tr>{headCells}</tr></thead>
          <tbody>
            {[0, 1, 2].map((i) => (
              <tr key={i}>
                {cols.filter((c) => visibleKeys.includes(c.key)).map((c) => <Fragment key={c.key}>{td(c.align, c.key === "description" ? `Regel ${i + 1}` : c.val)}</Fragment>)}
                {showCustom && <Fragment>{td("left", "—")}</Fragment>}
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    case "totals":
      return (
        <div style={fontWrap({ marginLeft: "auto", width: "65%", fontSize: baseSize })} className="space-y-0.5">
          <div className="flex justify-between"><span>Subtotaal</span><span>€ 0,00</span></div>
          <div className="flex justify-between"><span>BTW 21%</span><span>€ 0,00</span></div>
          <div className="flex justify-between"><span>BTW 9%</span><span>€ 0,00</span></div>
          <div className="flex justify-between font-bold" style={{ fontSize: baseSize + 3 }}><span>Totaal</span><span>€ 0,00</span></div>
        </div>
      );
    case "textBlock":
      return (
        <div
          style={fontWrap({ fontSize: comp.props?.fontSize || baseSize, textAlign: comp.props?.align || "left", whiteSpace: "pre-wrap" })}
          className="text-slate-700"
        >
          {comp.props?.text || <span className="text-slate-300">[ eigen tekst ]</span>}
        </div>
      );
    case "notes":
      return (
        <div style={fontWrap()}>
          <span className="text-slate-400" style={{ fontSize: Math.max(baseSize - 2, 8) }}>{comp.props?.label || "Opmerkingen"}</span>
          <br />
          <span className="text-slate-500" style={{ whiteSpace: "pre-wrap" }}>{comp.props?.text || "uit document"}</span>
        </div>
      );
    default:
      return null;
  }
}